import { Link } from "@tanstack/react-router";
import { Eye, EyeOff } from "lucide-react";
import { Fragment, type ReactNode } from "react";
import { ThemeToggle } from "../theme-toggle";
import {
  isNavigationSectionActive,
  navigationFor,
  type NavigationItem,
} from "../../lib/navigation";
import { cn } from "../../lib/utils";
import type { AuthUser } from "../../stores/auth";

interface DesktopHeaderProps {
  user: AuthUser | null;
  pathname: string;
  hideHeaderIdentity: boolean;
  identityPreferenceBusy: boolean;
  onToggleIdentity(): void;
  onLogout(): void;
}

export function DesktopHeader({
  user,
  pathname,
  hideHeaderIdentity,
  identityPreferenceBusy,
  onToggleIdentity,
  onLogout,
}: DesktopHeaderProps) {
  const navigation = user ? navigationFor(user) : [];
  const chapters = navigation.filter((section) => section.placement === "chapter");
  const utilities = navigation.filter((section) => section.placement === "utility");

  return (
    <header className="border-b border-border/70 px-4 h-14 flex items-center justify-between bg-bg/95 backdrop-blur">
      <div className="flex h-full items-center gap-5 min-w-0">
        <Link
          to="/"
          className="font-semibold tracking-[0.14em] shrink-0 text-fg hover:text-accent transition-colors"
        >
          CASCENA
        </Link>
        {user && (
          <nav className="flex h-full items-center gap-4 text-muted text-sm overflow-x-auto whitespace-nowrap min-w-0">
            {chapters.map((section) => {
              const active = isNavigationSectionActive(section.id, pathname);
              return (
                <Fragment key={section.id}>
                  <ChapterLink to={section.landingTo} active={active}>
                    {section.label}
                  </ChapterLink>
                  {active && <SubNav items={section.items} />}
                </Fragment>
              );
            })}
          </nav>
        )}
      </div>
      <div className="text-xs text-muted flex h-full items-center gap-3 shrink-0 border-l border-border/50 pl-4">
        {utilities.map((section) => (
          <NavLink key={section.id} to={section.landingTo}>
            {section.label}
          </NavLink>
        ))}
        <ThemeToggle />
        {user ? (
          <>
            <HeaderIdentityToggle
              hidden={user.hideIdentityInHeader}
              busy={identityPreferenceBusy}
              onToggle={onToggleIdentity}
            />
            <span className="max-w-[360px] truncate">
              {hideHeaderIdentity
                ? user.role
                : `${user.email} · ${user.companyName ?? "platform"} · ${user.role}`}
            </span>
            <button
              type="button"
              onClick={onLogout}
              className="text-muted hover:text-fg transition-colors"
            >
              logout
            </button>
          </>
        ) : (
          <Link to="/login" className="text-accent">
            login
          </Link>
        )}
      </div>
    </header>
  );
}

function HeaderIdentityToggle({
  hidden,
  busy,
  onToggle,
}: {
  hidden: boolean;
  busy: boolean;
  onToggle(): void;
}) {
  const Icon = hidden ? Eye : EyeOff;
  const label = hidden
    ? "Show email and company on other pages"
    : "Hide email and company on other pages";
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={busy}
      title={label}
      aria-label={label}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded border border-border/60",
        "text-muted hover:text-fg hover:border-accent/60 transition-colors disabled:opacity-50"
      )}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

function ChapterLink({
  to,
  active,
  children,
}: {
  to: string;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className={cn(
        "flex h-14 items-center border-b-2 border-transparent transition-colors hover:text-fg",
        active ? "border-accent text-fg font-semibold" : "text-muted"
      )}
    >
      {children}
    </Link>
  );
}

function SubNav({ items }: { items: NavigationItem[] }) {
  if (items.length === 0) return null;
  return (
    <span className="flex h-14 items-center gap-3 border-l border-border/60 pl-4 text-xs">
      {items.map((item) => (
        <NavLink key={item.to} to={item.to}>
          {item.label}
        </NavLink>
      ))}
    </span>
  );
}

function NavLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="flex h-14 items-center border-b-2 border-transparent px-0.5 hover:text-fg transition-colors"
      activeProps={{ className: "border-accent text-fg" }}
    >
      {children}
    </Link>
  );
}
